import React from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { StepStatus } from '../chatMessage'
import VISUALLY_HIDDEN from '../a11y'

export type AssistantStepEntryProps = {
  text: string
  stepStatus: StepStatus
}

// Per-status accessible prefix injected as visually-hidden text so screen
// readers announce success/error/in-progress alongside the step text. Without
// this, the icon is the only state cue and AT users hear the same step text
// regardless of outcome.
const STATUS_ANNOUNCEMENT: Record<StepStatus, string> = {
  pending: 'Step in progress',
  done: 'Step done',
  error: 'Step error'
}

export const iconByStatus: Record<StepStatus, React.ReactNode> = {
  pending: (
    <CircularProgress
      size={14}
      aria-label={STATUS_ANNOUNCEMENT.pending}
      sx={{ color: 'text.disabled', flexShrink: 0 }}
    />
  ),
  done: (
    <CheckCircleOutlineIcon
      aria-hidden
      sx={{ fontSize: 16, color: 'success.main', flexShrink: 0 }}
    />
  ),
  error: <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16, color: 'error.main', flexShrink: 0 }} />
}

/**
 * Inline step log entry rendered in the message list during DOM tool execution.
 * Shows a spinner while pending, a checkmark on success, and an error icon on failure.
 */
const AssistantStepEntry = ({ text, stepStatus }: AssistantStepEntryProps) => {
  const icon = iconByStatus[stepStatus]

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.5, mb: 0.5 }}>
      {icon}
      {stepStatus !== 'pending' && (
        <span style={VISUALLY_HIDDEN}>{STATUS_ANNOUNCEMENT[stepStatus]}: </span>
      )}
      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
        {text}
      </Typography>
    </Box>
  )
}

export default AssistantStepEntry
